// src/comp/DialogProvider.jsx
import React, { createContext, useContext, useState, useCallback } from "react";

const DialogContext = createContext();

export const DialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    content: "",
    confirmText: "확인",
    cancelText: "",
    onConfirm: null,
    onCancel: null,
  });

  const showDialog = useCallback((options) => {
    setDialog({
      open: true,
      title: options.title || "",
      content: options.content || "",
      confirmText: options.confirmText || "확인",
      cancelText: options.cancelText || "",
      onConfirm: () => {
        if (options.onConfirm) options.onConfirm();
        closeDialog();
      },
      onCancel: () => {
        if (options.onCancel) options.onCancel();
        closeDialog();
      },
    });
  }, []);

  const closeDialog = () => {
    setDialog((prev) => ({ ...prev, open: false }));
  };

  return (
    <DialogContext.Provider value={{ showDialog }}>
      {children}
      {dialog.open && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/40 z-[999999]"
          role="dialog"
          aria-modal="true"
          onClick={closeDialog}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 🔹 Header */}
            {dialog.title && (
              <div className="flex items-center gap-2 px-4 py-3 border-b bg-gray-50">
                {/* 아이콘 */}
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600">
                  <svg
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 9v4m0 4h.01" />
                    <circle cx="12" cy="12" r="9" />
                  </svg>
                </span>

                <h2
                  id="dialog-title"
                  className="text-sm font-semibold text-gray-800"
                >
                  {dialog.title}
                </h2>
              </div>
            )}

            {/* 🔹 Content */}
            <div
              id="dialog-content"
              className="px-5 py-4 text-sm text-gray-700 leading-relaxed"
            >
              {typeof dialog.content === "string" ? (
                <p>{dialog.content}</p>
              ) : (
                dialog.content
              )}
            </div>

            {/* 🔹 Footer */}
            <div className="flex justify-end gap-2 px-4 py-3 bg-gray-50 border-t">
              {dialog.cancelText && (
                <button
                  onClick={dialog.onCancel}
                  className="px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-200 transition"
                >
                  {dialog.cancelText}
                </button>
              )}
              <button
                onClick={dialog.onConfirm}
                className="px-4 py-1.5 rounded-md text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium transition"
              >
                {dialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};

export const useDialog = () => useContext(DialogContext);
